import {
  Component,
  ElementRef,
  Input,
  OnInit,
  HostListener,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';
import { TourService, TourStep } from '@services/tour.service';
import { animate, style, transition, trigger } from '@angular/animations';

@Component({
    selector: 'app-tour-bubble',
    templateUrl: './tour-bubble.component.html',
    styleUrls: ['./tour-bubble.component.scss'],
    animations: [
        trigger('fadeInOut', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('0.3s', style({ opacity: 1 })),
            ]),
            transition(':leave', [animate('0.3s', style({ opacity: 0 }))]),
        ]),
    ],
    standalone: false
})
export class TourBubbleComponent implements OnInit, OnDestroy, AfterViewInit {
  public animationState = 'enter';
  @Input() step: TourStep;
  @Input() tourId: string;
  arrowPosition: string;
  scrollContainer: any;
  scrollRetry = 30;

  constructor(
    public elementRef: ElementRef,
    private tourService: TourService
  ) {}

  ngOnInit() {
    this.updatePosition();
  }

  ngAfterViewInit() {
    this.setScrollContainer();
    this.updatePosition();
  }

  private setScrollContainer() {
    if (this.step && this.step.element) {
      let parent = this.step.element.nativeElement.parentNode;
      while (parent) {
        if (parent.id === 'App') {
          break;
        }
        const style = window.getComputedStyle(parent);
        if (
          style.overflow === 'auto' ||
          style.overflow === 'scroll' ||
          style.overflowY === 'auto' ||
          style.overflowY === 'scroll'
        ) {
          if (parent.scrollHeight > parent.clientHeight) {
            this.scrollContainer = parent;
            this.scrollContainer.addEventListener(
              'scroll',
              this.handleScroll.bind(this)
            );
            break;
          }
        }
        parent = parent.parentNode;
      }
      if (!this.scrollContainer) {
        this.scrollRetry--;
        if (this.scrollRetry > 0) {
          setTimeout(() => {
            this.setScrollContainer();
          }, 1000);
        }
      }
    }
  }

  handleScroll() {
    this.updatePosition();
  }

  @HostListener('window:resize', ['$event'])
  handleWindowEvent() {
    this.updatePosition();
  }

  ngOnDestroy() {
    if (this.scrollContainer) {
      this.scrollContainer.removeEventListener(
        'scroll',
        this.handleScroll.bind(this)
      );
    }
  }

  updatePosition() {
    if (this.step && this.step.element) {
      const rect = this.step.element.nativeElement.getBoundingClientRect();
      const selfRect = {
        width: this.elementRef.nativeElement.scrollWidth,
        height: this.elementRef.nativeElement.scrollHeight,
        top: this.elementRef.nativeElement.offsetTop,
        left: this.elementRef.nativeElement.offsetLeft,
      };
      let position: any = {};
      let positionSetting = this.step.position.split('-');
      let offset;
      switch (positionSetting[0]) {
        case 'top':
          offset = this.getWidthOffset(positionSetting[1], rect);
          position = {
            left: rect.left + offset,
            top: rect.top - selfRect.height,
          };
          this.arrowPosition = 'bottom';
          break;
        case 'bottom':
          offset = this.getHeightOffset(positionSetting[1], rect);
          position = {
            left: rect.left + offset,
            top: rect.bottom,
          };
          this.arrowPosition = 'top';
          break;
        case 'left':
          offset = this.getHeightOffset(positionSetting[1], rect);
          position = {
            top: rect.top + offset,
            left: rect.left - selfRect.width + 10,
          };
          this.arrowPosition = 'left';
          break;
        case 'right':
          offset = this.getHeightOffset(positionSetting[1], rect);
          position = {
            top: rect.top + offset,
            left: rect.left + rect.width - 10,
          };
          this.arrowPosition = 'right';
          break;
      }
      if (positionSetting[1]) {
        this.arrowPosition += ' adjust-' + positionSetting[1];
      }

      this.elementRef.nativeElement.style.position = 'absolute';
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Ensure the element is not positioned outside of the visible screen area
      const adjustedLeft = Math.min(
        Math.max(16, position.left),
        viewportWidth - selfRect.width
      );
      const adjustedTop = Math.min(
        Math.max(16, position.top),
        viewportHeight - selfRect.height
      );
      if (position.top + selfRect.height < adjustedTop) {
        this.arrowPosition += ' move-top';
      }
      if (adjustedTop < position.top) {
        this.arrowPosition += ' move-bottom';
      }
      this.elementRef.nativeElement.style.left = adjustedLeft + 'px';
      this.elementRef.nativeElement.style.top = adjustedTop + 'px';
    }
  }

  getHeightOffset(positionSetting: string, rect: any) {
    let offset = 0;
    switch (positionSetting) {
      case 'center':
        offset = rect.height / 2;
        break;
      case 'top':
        offset = -10;
        break;
      case 'bottom':
        offset = rect.height;
        break;
      default:
        if (positionSetting && !isNaN(parseInt(positionSetting))) {
          offset = parseInt(positionSetting);
        } else {
          offset = 0;
        }
        break;
    }
    return offset;
  }

  getWidthOffset(positionSetting: string, rect: any) {
    let offset = 0;
    switch (positionSetting) {
      case 'center':
        offset = rect.width / 2;
        break;
      case 'left':
        offset = 0;
        break;
      case 'right':
        offset = rect.width;
        break;
      default:
        if (positionSetting && !isNaN(parseInt(positionSetting))) {
          offset = parseInt(positionSetting);
        } else {
          offset = 0;
        }
        break;
    }
    return offset;
  }

  nextStep() {
    this.tourService.nextStep(this.tourId, this.step.stepId);
  }

  closeTourBubble() {
    this.tourService.endTour(this.tourId);
  }
}
